// src/services/authError.ts
//
// Traduz o erro de uma request de autenticação (login, login Microsoft,
// solicitação/confirmação de reset de senha) na mensagem + variante de alerta
// a exibir. Centraliza a lógica que antes se repetia nos catches das 3 telas
// de auth (Login, RedefinirSenha, DefinirNovaSenha).
//
// O caso que motivou o helper: o backend passou a devolver HTTP 429 (rate-limit
// por IP) nessas rotas — corpo `{ error, reason: "rate_limited",
// retryAfterSeconds }`. Sem tratamento, o 429 cairia no mesmo alerta vermelho de
// "usuário ou senha inválido", fazendo o usuário achar que errou a senha e
// insistir — o que ESTENDE o bloqueio. Pior ainda porque o lockout é por IP: um
// escritório inteiro atrás do mesmo IP compartilha o contador, então quem vê a
// mensagem pode não ter errado nada. Por isso o 429 usa variante `warning`
// ("aguarde") em vez de `error` ("você errou"), e a mensagem não acusa ninguém.
//
// Retorna um objeto puro (sem efeito colateral) para ser testável sem React;
// as telas passam o resultado a `showAlert`.

// Subconjunto das variantes de `showAlert`/`AlertDialog` que este helper emite.
export type AuthAlertVariant = 'error' | 'warning';

export interface AuthErrorAlert {
  message: string;
  variant: AuthAlertVariant;
}

interface ApiErrorBody {
  error?: string;
  reason?: string;
  retryAfterSeconds?: number;
}

interface ApiErrorLike {
  response?: {
    status?: number;
    data?: ApiErrorBody;
  };
}

// `reason` do corpo do 429 (ver AuthRateLimitFilter no api-blueltSystem).
const RATE_LIMITED_REASON = 'rate_limited';

// Formata os segundos de espera de forma amigável. Vazio se não houver valor útil.
function formatWait(seconds: number | undefined): string {
  if (typeof seconds !== 'number' || !Number.isFinite(seconds) || seconds <= 0) {
    return '';
  }
  if (seconds < 60) {
    const s = Math.ceil(seconds);
    return `${s} segundo${s === 1 ? '' : 's'}`;
  }
  const min = Math.ceil(seconds / 60);
  return `${min} minuto${min === 1 ? '' : 's'}`;
}

/**
 * Resolve a mensagem + variante de alerta para um erro de request de auth.
 *
 * - HTTP 429 (ou corpo com `reason: "rate_limited"`): variante `warning` e
 *   mensagem que orienta a aguardar — incluindo o tempo de `retryAfterSeconds`
 *   quando presente. Não acusa o usuário de credencial errada.
 * - Qualquer outro erro: variante `error` e a mensagem do backend
 *   (`response.data.error`) ou o `fallback` da tela.
 *
 * @param error    o erro capturado no catch (shape de erro axios).
 * @param fallback mensagem padrão da tela quando o backend não envia `error`.
 */
export function resolveAuthError(error: unknown, fallback: string): AuthErrorAlert {
  const response = (error as ApiErrorLike | undefined)?.response;
  const data = response?.data;
  const isRateLimited = response?.status === 429 || data?.reason === RATE_LIMITED_REASON;

  if (isRateLimited) {
    const wait = formatWait(data?.retryAfterSeconds);
    const message = wait
      ? `Muitas tentativas seguidas. Aguarde ${wait} antes de tentar novamente.`
      : 'Muitas tentativas seguidas. Aguarde alguns minutos antes de tentar novamente.';
    return { message, variant: 'warning' };
  }

  return { message: data?.error || fallback, variant: 'error' };
}
